import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { avatarFor } from "@/lib/utils-tg";
import { Linkedin, Loader2, Image as ImageIcon, Sparkles, Building2, Plus, Check, UserPlus, Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import InviteUserDialog from "@/components/InviteUserDialog";

export default function SettingsPage() {
  const { user, setRole, updateMe } = useAuth();
  const { companies, active, setActiveId } = useCompany();
  const [form, setForm] = useState({
    name: user?.name || "",
    linkedin_url: user?.linkedin_url || "",
    avatar_url: user?.avatar_url || "",
  });
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  const loadMembers = async () => {
    if (!active?.id) return;
    try {
      const r = await api.get(`/companies/${active.id}/members`);
      setMembers(r.data || []);
    } catch {
      setMembers([]);
    }
  };

  useEffect(() => {
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  const save = async () => {
    setBusy(true);
    try {
      await updateMe({
        name: form.name,
        linkedin_url: form.linkedin_url,
        avatar_url: form.avatar_url,
      });
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const importFromLinkedIn = async () => {
    if (!form.linkedin_url.trim()) {
      toast.error("Paste your LinkedIn URL first");
      return;
    }
    setImporting(true);
    try {
      const updated = await updateMe({
        linkedin_url: form.linkedin_url,
        auto_import_linkedin_avatar: true,
      });
      setForm({ ...form, avatar_url: updated.avatar_url || "" });
      toast.success("LinkedIn photo imported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't import — check the URL");
    } finally {
      setImporting(false);
    }
  };

  const changePassword = async () => {
    if (pwd.next.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setPwdBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      toast.success("Password changed");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change password");
    } finally {
      setPwdBusy(false);
    }
  };

  const previewSrc = form.avatar_url || user?.avatar_url || avatarFor(user?.name || "U");

  return (
    <div className="px-6 md:px-10 pt-6 max-w-3xl mx-auto pb-12" data-testid="settings-page">
      <h1 className="font-display text-3xl font-extrabold text-slate-900">Settings</h1>
      <p className="text-slate-500 mt-1 mb-6">Profile, businesses, and security.</p>

      {/* Profile section */}
      <div className="tg-card p-6 mb-4" data-testid="settings-profile">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-4">Profile</div>

        <div className="flex items-start gap-5 flex-wrap">
          <div className="flex flex-col items-center gap-2">
            <div className="h-24 w-24 rounded-full overflow-hidden ring-2 ring-emerald-100 bg-slate-100 shrink-0">
              <img
                key={previewSrc}
                src={previewSrc}
                onError={(e) => { e.currentTarget.src = avatarFor(user?.name || "U"); }}
                alt="Avatar preview"
                className="h-full w-full object-cover"
                data-testid="avatar-preview"
              />
            </div>
            <span className="text-[10px] text-slate-400">Preview</span>
          </div>

          <div className="flex-1 min-w-[280px] space-y-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Display name</label>
              <input
                className="tg-input mt-1"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="settings-name-input"
              />
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <Linkedin className="h-3.5 w-3.5" /> LinkedIn URL
              </label>
              <div className="flex gap-2 mt-1">
                <input
                  className="tg-input flex-1"
                  placeholder="https://www.linkedin.com/in/your-handle"
                  value={form.linkedin_url}
                  onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })}
                  data-testid="settings-linkedin-input"
                />
                <button
                  type="button"
                  onClick={importFromLinkedIn}
                  disabled={importing || !form.linkedin_url.trim()}
                  className="tg-btn-secondary disabled:opacity-50 whitespace-nowrap"
                  data-testid="settings-import-linkedin"
                  title="Pull profile photo from LinkedIn"
                >
                  {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-emerald-600" />}
                  Import photo
                </button>
              </div>
            </div>

            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <ImageIcon className="h-3.5 w-3.5" /> Avatar image URL
              </label>
              <input
                className="tg-input mt-1"
                placeholder="https://… (auto-filled by Import photo)"
                value={form.avatar_url}
                onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                data-testid="settings-avatar-input"
              />
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={save}
                disabled={busy}
                className="tg-btn-primary disabled:opacity-50"
                data-testid="settings-save"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Businesses section */}
      <div className="tg-card p-6 mb-4" data-testid="settings-businesses">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Businesses</div>
          <span className="text-xs text-slate-400">{companies.length} {companies.length === 1 ? "company" : "companies"}</span>
        </div>

        {companies.length === 0 && (
          <div className="text-sm text-slate-500 bg-slate-50 rounded-xl p-4 text-center">
            You don't belong to any company yet. Create one from the top-left switcher to start logging work.
          </div>
        )}

        <div className="space-y-2">
          {companies.map((c) => {
            const isActive = c.id === active?.id;
            return (
              <div
                key={c.id}
                className={`flex items-center justify-between gap-3 p-3 rounded-xl border ${isActive ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
                data-testid={`business-row-${c.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    <Building2 className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5 truncate">
                      {c.name}
                      {c.is_owner && <Crown className="h-3 w-3 text-amber-500" />}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {c.work_email || user?.email} · <span className="capitalize">{c.role}</span>
                    </div>
                  </div>
                </div>
                {isActive ? (
                  <span className="tg-pill bg-emerald-50 text-emerald-700 border-emerald-200">
                    <Check className="h-3 w-3" /> Active
                  </span>
                ) : (
                  <button
                    onClick={() => setActiveId(c.id)}
                    className="tg-btn-secondary text-xs"
                    data-testid={`switch-to-${c.id}`}
                  >
                    Switch to
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {active?.role === "admin" && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                <UserPlus className="h-3.5 w-3.5" /> Members of {active.name}
              </div>
              <button
                onClick={() => setInviteOpen(true)}
                className="tg-btn-primary text-xs"
                data-testid="invite-open"
              >
                <Plus className="h-3.5 w-3.5" /> Invite user
              </button>
            </div>
            {members.length > 0 && (
              <div className="space-y-1.5">
                {members.map((m) => (
                  <div
                    key={m.id}
                    className="flex items-center justify-between gap-2 text-xs text-slate-600 px-3 py-1.5 rounded-lg bg-slate-50"
                    data-testid={`member-${m.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-semibold truncate">{m.name || m.work_email}</span>
                      <span className="text-slate-400 truncate">· {m.work_email}</span>
                      <span className="text-slate-400 truncate hidden sm:inline">· <span className="capitalize">{m.role}</span></span>
                    </div>
                    <span className={`tg-pill text-[10px] ${m.status === "active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                      {m.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <InviteUserDialog
              open={inviteOpen}
              onClose={() => setInviteOpen(false)}
              companyName={active.name}
              onInvited={loadMembers}
            />
          </div>
        )}
      </div>

      {/* Account section */}
      <div className="tg-card p-6 mb-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Account</div>
        <div className="text-base font-semibold text-slate-900">{user?.name}</div>
        <div className="text-sm text-slate-500">{user?.email}</div>
        <div className="text-[11px] text-slate-400 mt-1">Sign in with this email or any company work email.</div>
      </div>

      {/* Change Password section */}
      <div className="tg-card p-6 mb-4" data-testid="settings-password">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5" /> Change password
        </div>
        <div className="space-y-3">
          <input
            type="password"
            className="tg-input"
            placeholder="Current password"
            value={pwd.current}
            onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
            data-testid="password-current"
          />
          <input
            type="password"
            className="tg-input"
            placeholder="New password (min 6 chars)"
            value={pwd.next}
            onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
            data-testid="password-next"
          />
          <input
            type="password"
            className="tg-input"
            placeholder="Confirm new password"
            value={pwd.confirm}
            onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
            data-testid="password-confirm"
          />
          <div className="flex justify-end">
            <button
              onClick={changePassword}
              disabled={pwdBusy || !pwd.current || !pwd.next}
              className="tg-btn-primary disabled:opacity-50"
              data-testid="password-submit"
            >
              {pwdBusy && <Loader2 className="h-4 w-4 animate-spin" />} Change password
            </button>
          </div>
        </div>
      </div>

      {/* Role section */}
      <div className="tg-card p-6">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Role</div>
        <div className="flex gap-2 flex-wrap">
          {["employee", "supervisor", "advisor", "founder", "admin"].map((r) => (
            <button
              key={r}
              onClick={() => setRole(r)}
              className={`tg-pill capitalize ${
                user?.role === r
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`settings-role-${r}`}
            >
              {r}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3">
          Role only affects what you can see in MVP — full permissions come later.
        </p>
      </div>
    </div>
  );
}
